/**
 * Session-Kontext: laedt `GET /api/v1/me` und haelt den angemeldeten User
 * plus seine Mitgliedschaften vor. Der aktive Mandant liegt daneben, damit
 * spaetere Phasen ohne Umbau Queues und Tickets daran haengen koennen.
 */
import type { CurrentUser, SessionMembership } from '@tickethub/shared';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { applyAccountLocale } from '@/i18n';
import { ApiError } from './api';
import { meApi } from './me';

const ACTIVE_TENANT_KEY = 'tickethub.activeTenant';

type SessionState = {
  user: CurrentUser | null;
  loading: boolean;
  activeMembership: SessionMembership | null;
  setActiveTenant: (tenantId: string) => void;
  /**
   * Uebernimmt eine bereits geladene Fassung des Kontos. `PATCH /v1/me`
   * antwortet mit demselben Objekt wie `GET /v1/me` — ein zweiter Roundtrip
   * (und der Ladezustand, der die ganze Oberflaeche kurz austauscht) waere
   * verschenkt.
   */
  applyUser: (user: CurrentUser) => void;
  reload: () => Promise<void>;
  clear: () => void;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_TENANT_KEY),
  );

  /** Holt die Session. Setzt keinen State *vor* dem ersten `await`. */
  const load = useCallback(async () => {
    try {
      const loaded = await meApi.load();
      // Auf einem frischen Geraet gilt die im Konto hinterlegte Sprache —
      // sonst waere `users.locale` ein Feld, das nur geschrieben wird.
      applyAccountLocale(loaded.locale);
      setUser(loaded);
    } catch (error) {
      // 401 ist der Normalfall „nicht angemeldet" und kein Fehler.
      if (!(error instanceof ApiError && error.isUnauthorized)) throw error;
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    await load();
  }, [load]);

  useEffect(() => {
    // `load` setzt State erst *nach* dem Netzwerk-Await, es entsteht also keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  const setActiveTenant = useCallback((tenantId: string) => {
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    setActiveTenantId(tenantId);
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(ACTIVE_TENANT_KEY);
    setActiveTenantId(null);
    setUser(null);
  }, []);

  const activeMembership = useMemo(() => {
    if (!user || user.memberships.length === 0) return null;
    return (
      user.memberships.find((membership) => membership.tenant_id === activeTenantId) ??
      user.memberships[0] ??
      null
    );
  }, [user, activeTenantId]);

  const value = useMemo<SessionState>(
    () => ({ user, loading, activeMembership, setActiveTenant, applyUser: setUser, reload, clear }),
    [user, loading, activeMembership, setActiveTenant, reload, clear],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionState {
  const context = use(SessionContext);
  if (!context) throw new Error('useSession must be used inside SessionProvider');
  return context;
}
